/**
 * timingCellEvents — pure resolvers that turn a clicked timing/dock cell
 * (hourIndex + metricField) into the modal row objects MoveTable/AndonTable render.
 *
 * Unlike heatmapCellEvents.js (which reverses the bucketing from raw event lists),
 * these resolvers do NOT re-bucket: buildTimingRows/buildDockTimingRows already
 * retained the per-hour×metric contributors in `rows[hourIndex]._contributors`, so
 * the resolver is a plain lookup + map. This module imports nothing from the builders;
 * the rows are passed in, so it is trivially unit-testable with hand-built fixtures.
 *
 * Every mapped row is `status:'completed'` with a non-undefined `eventId` and the full
 * MoveTable/AndonTable field contract (so no column renders a blank Tag or stray em-dash).
 */

/**
 * Map a timing move contributor ({create,start,complete,duration}) to a MoveTable row.
 * The metric's two anchors drive startTs/completeTs:
 *   - createToStart:    create → start
 *   - createToComplete: create → complete
 *   - startToComplete:  start  → complete
 * @param {Object} c
 * @param {string} metricField
 * @returns {Object} MoveTable row
 */
function toMoveRow(c, metricField) {
    const { create, start, complete, duration } = c;
    let startTs;
    let completeTs;
    if (metricField === 'startToComplete') {
        startTs = start?.timestamp ?? null;
        completeTs = complete?.timestamp ?? null;
    } else if (metricField === 'createToStart') {
        startTs = create?.timestamp ?? null;
        completeTs = start?.timestamp ?? null;
    } else { // createToComplete
        startTs = create?.timestamp ?? null;
        completeTs = complete?.timestamp ?? null;
    }
    return {
        kind: 'move',
        status: 'completed',
        eventId: complete?.eventId ?? create?.eventId ?? start?.eventId ?? '',
        userId: complete?.userId ?? start?.userId ?? '',
        vehicleNumber: start?.vehicleNumber || complete?.vehicleNumber || create?.vehicleNumber || '',
        fromLocation: start?.location ?? '',
        toLocation: complete?.location ?? '',
        startTs,
        completeTs,
        duration,
        cancelTime: null,
        bucketTs: complete?.timestamp ?? null,
    };
}

/**
 * Map a timing andon contributor ({start,complete,suffix,duration}) to an AndonTable row.
 * These are START→COMPLETE pairings, so the row is always completed.
 * @param {Object} c
 * @returns {Object} AndonTable row
 */
function toAndonRow(c) {
    const { start, complete, suffix, duration } = c;
    return {
        kind: 'andon',
        status: 'completed',
        eventId: complete?.eventId ?? start?.eventId ?? '',
        suffix: suffix ?? '',
        location: start?.location ?? complete?.location ?? '',
        vehicleNumber: start?.vehicleNumber || complete?.vehicleNumber || '',
        userId: complete?.userId || start?.userId || '',
        startTs: start?.timestamp ?? null,
        completeTs: complete?.timestamp ?? null,
        duration,
        cancelTime: null,
        bucketTs: complete?.timestamp ?? null,
    };
}

// Per-metric anchor labels + endpoint accessors for dock contributors.
const DOCK_ANCHORS = {
    completeToTdrDock: {
        fromLocation: 'Complete',
        toLocation: 'TDR Dock',
        startTs: c => c.complete?.timestamp ?? null,
        completeTs: c => c.tdrDock?.timestamp ?? null,
    },
    loadCompleteToCreate: {
        fromLocation: 'Detach',
        toLocation: 'Create',
        startTs: c => c.detach?.timestamp ?? null,
        completeTs: c => c.create?.timestamp ?? null,
    },
    loadCompleteToCreateOver30: {
        fromLocation: 'Detach',
        toLocation: 'Create',
        startTs: c => c.detach?.timestamp ?? null,
        completeTs: c => c.create?.timestamp ?? null,
    },
    tdrReleaseToCreate: {
        fromLocation: 'TDR Release',
        toLocation: 'Create',
        startTs: c => c.tdrRelease?.timestamp ?? null,
        completeTs: c => c.create?.timestamp ?? null,
    },
};

/**
 * Map a dock contributor to a MoveTable row, using per-metric anchor labels for From/To.
 * @param {Object} c - dock contributor
 * @param {string} metricField
 * @returns {Object} MoveTable row
 */
function toDockRow(c, metricField) {
    const anchors = DOCK_ANCHORS[metricField] ?? DOCK_ANCHORS.completeToTdrDock;
    const completeTs = anchors.completeTs(c);
    return {
        kind: 'move',
        status: 'completed',
        eventId: c.complete?.eventId ?? c.create?.eventId ?? c.tdrDock?.eventId
            ?? c.tdrRelease?.eventId ?? c.detach?.eventId ?? '',
        userId: c.create?.userId ?? c.complete?.userId ?? '',
        vehicleNumber: c.detach?.vehicleNumber || c.create?.vehicleNumber
            || c.complete?.vehicleNumber || c.tdrDock?.vehicleNumber || c.tdrRelease?.vehicleNumber || '',
        fromLocation: anchors.fromLocation,
        toLocation: anchors.toLocation,
        startTs: anchors.startTs(c),
        completeTs,
        duration: c.duration,
        cancelTime: null,
        bucketTs: completeTs,
    };
}

/**
 * Resolve a clicked timing-card cell into its modal rows.
 * @param {Array} rows - buildTimingRows output (carries _contributors per row)
 * @param {number} hourIndex - slot index
 * @param {'createToStart'|'startToComplete'|'createToComplete'|'andonResponse'} metricField
 * @returns {Array<Object>} MoveTable (or AndonTable for andonResponse) rows; [] when empty
 */
export function resolveTimingCell(rows, hourIndex, metricField) {
    const contributors = rows?.[hourIndex]?._contributors?.[metricField] ?? [];
    if (metricField === 'andonResponse') {
        return contributors.map(toAndonRow);
    }
    return contributors.map(c => toMoveRow(c, metricField));
}

/**
 * Resolve a clicked dock-card cell into its modal rows.
 * @param {Array} rows - buildDockTimingRows output (carries _contributors per row)
 * @param {number} hourIndex - slot index
 * @param {'completeToTdrDock'|'loadCompleteToCreate'|'tdrReleaseToCreate'|'loadCompleteToCreateOver30'} metricField
 * @returns {Array<Object>} MoveTable rows; [] when empty
 */
export function resolveDockCell(rows, hourIndex, metricField) {
    const contributors = rows?.[hourIndex]?._contributors?.[metricField] ?? [];
    return contributors.map(c => toDockRow(c, metricField));
}
